import { getCookieConsentFromHeader, getCookieConsentSourceFromHeader } from "./cookieConsent";

export const REDDIT_ADS_CLICK_ID_QUERY_PARAM = "rdt_cid";
export const REDDIT_ADS_CLICK_ID_STORAGE_KEY = "tapestry:redditAds:rdt_cid";
const INTERNAL_URL_ORIGIN = "https://ads.openai.com";
const MAX_REDDIT_ADS_CLICK_ID_LENGTH = 256;
const REDDIT_ADS_ROLLOUT_COUNTRIES = new Set(["AU", "CA", "NZ", "US"]);

export function isRedditAdsRolloutCountry(countryCode: string | undefined): boolean {
  return countryCode != null && REDDIT_ADS_ROLLOUT_COUNTRIES.has(countryCode.toUpperCase());
}

export function isRedditAdsServerAttributionEligible({
  cookieHeader,
  globalPrivacyControlHeader,
  countryHeader,
}: {
  cookieHeader?: string;
  globalPrivacyControlHeader?: string | string[] | null;
  countryHeader?: string | string[] | null;
}): boolean {
  const globalPrivacyControl = getSingleHeaderValue(globalPrivacyControlHeader);
  const countryCode = getSingleHeaderValue(countryHeader)?.trim() || undefined;
  return (
    globalPrivacyControl !== "1" &&
    getCookieConsentFromHeader(cookieHeader ?? "", "marketing_consent") === "agreed" &&
    getCookieConsentSourceFromHeader(cookieHeader ?? "", "marketing_consent_source") ===
      "signed_out" &&
    isRedditAdsRolloutCountry(countryCode)
  );
}

export function getRedditAdsClickId(searchParams: URLSearchParams): string | undefined {
  const values = searchParams.getAll(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
  if (values.length !== 1) return undefined;

  const rawValue = values[0];
  const value = rawValue?.trim();
  if (!value || value !== rawValue || !isValidRedditAdsClickId(value)) {
    return undefined;
  }
  return value;
}

export function appendRedditAdsClickIdToPath(path: string, clickId: string | undefined): string {
  try {
    const url = new URL(path, INTERNAL_URL_ORIGIN);
    if (url.origin !== INTERNAL_URL_ORIGIN) return path;

    url.searchParams.delete(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
    if (clickId && isValidRedditAdsClickId(clickId)) {
      url.searchParams.set(REDDIT_ADS_CLICK_ID_QUERY_PARAM, clickId);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

function getSingleHeaderValue(
  headerValue: string | string[] | null | undefined,
): string | undefined {
  return Array.isArray(headerValue) ? headerValue[0] : headerValue ?? undefined;
}

function isValidRedditAdsClickId(value: string): boolean {
  return (
    value.trim() === value &&
    value.length > 0 &&
    value.length <= MAX_REDDIT_ADS_CLICK_ID_LENGTH &&
    !Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f;
    })
  );
}
